import type { FixtureStatistics, TeamMatchStats } from '@h2h/shared';
import { cn } from '../../lib/cn';
import { fmtStat } from './helpers';

const STAT_ROWS: { key: keyof TeamMatchStats; label: string; percent?: boolean; decimals?: number }[] = [
  { key: 'possession', label: 'Possession', percent: true },
  { key: 'shots', label: 'Total shots' },
  { key: 'shotsOnTarget', label: 'Shots on target' },
  { key: 'xg', label: 'Expected goals (xG)', decimals: 1 },
  { key: 'corners', label: 'Corners' },
  { key: 'fouls', label: 'Fouls' },
  { key: 'offsides', label: 'Offsides' },
  { key: 'yellowCards', label: 'Yellow cards' },
  { key: 'redCards', label: 'Red cards' },
  { key: 'saves', label: 'Saves' },
  { key: 'passAccuracy', label: 'Pass accuracy', percent: true }
];

/** Per-team match statistics with a comparative split bar per row. */
export function MatchStats({ stats, live }: { stats: FixtureStatistics; live: boolean }) {
  const rows = STAT_ROWS.filter((r) => stats.home[r.key] != null || stats.away[r.key] != null);
  if (rows.length === 0) return null;
  return (
    <>
      <div className="flex items-center justify-between pt-[22px]">
        <span className="text-[13px] font-bold tracking-snug text-white">Match stats</span>
        {live && (
          <span className="inline-flex items-center gap-[5px] rounded-md bg-accent/[0.16] px-2 py-[3px]">
            <span className="h-[5px] w-[5px] animate-h2h-pulse rounded-full bg-accent" />
            <span className="text-[10px] font-extrabold uppercase tracking-eyebrow text-accent">Live</span>
          </span>
        )}
      </div>
      <div className="mt-3 flex flex-col gap-[14px] rounded-xl border border-white/[0.08] bg-white/[0.05] p-4">
        {rows.map((r) => (
          <StatRow
            key={r.key}
            label={r.label}
            home={stats.home[r.key]}
            away={stats.away[r.key]}
            percent={r.percent}
            decimals={r.decimals}
          />
        ))}
      </div>
    </>
  );
}

function StatRow({
  label,
  home,
  away,
  percent,
  decimals
}: {
  label: string;
  home: number | null;
  away: number | null;
  percent?: boolean;
  decimals?: number;
}) {
  const h = home ?? 0;
  const a = away ?? 0;
  // Share of the bar for the home side; default to an even split when both are 0.
  const total = h + a;
  const homePct = percent ? h : total > 0 ? (h / total) * 100 : 50;
  const homeLeads = h > a;
  const awayLeads = a > h;
  return (
    <div>
      <div className="flex items-center justify-between text-[13px] font-extrabold tabular-nums">
        <span className={cn('num', homeLeads ? 'text-white' : 'text-white/40')}>{fmtStat(home, percent, decimals)}</span>
        <span className="text-[11px] font-semibold text-white/55">{label}</span>
        <span className={cn('num', awayLeads ? 'text-white' : 'text-white/40')}>{fmtStat(away, percent, decimals)}</span>
      </div>
      <div className="mt-[6px] flex h-[5px] gap-[3px]">
        <div className="flex flex-1 justify-end overflow-hidden rounded-full bg-white/[0.08]">
          <div
            className={cn('h-full rounded-full', homeLeads ? 'bg-accent' : 'bg-white/25')}
            style={{ width: `${Math.max(0, Math.min(100, homePct))}%` }}
          />
        </div>
        <div className="flex flex-1 overflow-hidden rounded-full bg-white/[0.08]">
          <div
            className={cn('h-full rounded-full', awayLeads ? 'bg-accent' : 'bg-white/25')}
            style={{ width: `${Math.max(0, Math.min(100, 100 - homePct))}%` }}
          />
        </div>
      </div>
    </div>
  );
}
